import { useMemo } from "react";
import { Sparkline } from "./Sparkline";
import { formatDateTime, formatRelative } from "../../lib/format";
import type { InvestorCard, TimeseriesResponse } from "../../lib/api/types";
import type { RangeWindow } from "../../lib/range";

interface LeaderboardProps {
  investors: InvestorCard[];
  colorById: Record<string, string>;
  stats: TimeseriesResponse | undefined;
  window: RangeWindow;
  onOpen: (userId: string) => void;
}

/** Ranked, compact investor list — downloads desc, then opens desc (PRD §7.3). */
export function Leaderboard({ investors, colorById, stats, window, onOpen }: LeaderboardProps) {
  const ranked = useMemo(
    () => [...investors].sort((a, b) => b.downloads - a.downloads || b.opens - a.opens),
    [investors],
  );

  const currentBuckets = useMemo(() => {
    const buckets = stats?.buckets ?? [];
    const splitYmd = window.currentFromYmd;
    return window.days != null && splitYmd != null
      ? buckets.filter((b) => b.date >= splitYmd)
      : buckets;
  }, [stats, window]);

  const sparkFor = (userId: string): number[] =>
    currentBuckets.map((b) => b.values[userId]?.downloads ?? 0);

  return (
    <div className="overflow-hidden rounded-lg border border-surface-border bg-surface">
      <ul className="divide-y divide-surface-border">
        {ranked.map((inv, i) => {
          const color = colorById[inv.userId] ?? "#6b7280";
          const inactive = inv.opens === 0 && inv.downloads === 0;
          return (
            <li key={inv.userId}>
              <button
                type="button"
                onClick={() => onOpen(inv.userId)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
              >
                <span className="w-4 shrink-0 text-meta text-text-muted">{i + 1}</span>
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: color }}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-text">{inv.displayName}</span>
                  <span className="block text-meta text-text-muted">
                    {inactive
                      ? "No activity yet"
                      : inv.lastActive
                        ? `Last active ${formatDateTime(inv.lastActive)} · ${formatRelative(inv.lastActive)}`
                        : "No activity yet"}
                  </span>
                </span>

                <span className="hidden shrink-0 sm:block">
                  <Sparkline data={sparkFor(inv.userId)} color={color} width={88} height={26} />
                </span>

                <span className="shrink-0 text-right">
                  <span className="block text-body font-semibold text-brand-700">{inv.opens}</span>
                  <span className="block text-meta text-text-muted">opens</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-body font-semibold text-success-700">{inv.downloads}</span>
                  <span className="block text-meta text-text-muted">downloads</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
